/** @jsxImportSource react */
import { useEffect, useState } from 'react'
import { Alert, Box, Button, FormControlLabel, Stack, Switch, Typography } from '@mui/material'
import { pushAPI } from '../../api/crm'
import { getErrorMessage } from '../../utils/apiErrors'
import {
  disablePush, enablePush, installState, onInstallChange, promptInstall, pushEnabledHere, pushSupported,
} from '../../pwa'
import { useNotify } from './NotificationProvider'

/** This device: install the app, and push notifications on or off. */
const DeviceSettings = () => {
  const notify = useNotify()
  const [install, setInstall] = useState(installState())
  const [push, setPush] = useState(null)   // null while checking
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => onInstallChange(() => setInstall(installState())), [])
  useEffect(() => { pushEnabledHere().then(setPush).catch(() => setPush(false)) }, [])

  const toggle = async (on) => {
    setBusy(true); setError('')
    try {
      if (on) { await enablePush(); notify('Notifications on for this device') } else { await disablePush(); notify('Notifications off for this device') }
      setPush(on)
    } catch (e) {
      setError(e?.response ? getErrorMessage(e) : e.message)
    } finally {
      setBusy(false)
    }
  }
  const test = async () => {
    try {
      const r = await pushAPI.test()
      notify(r.sent ? 'Test notification sent' : 'No device took it; turn notifications off and on again', r.sent ? 'success' : 'warning')
    } catch (e) {
      setError(getErrorMessage(e))
    }
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>This device</Typography>
      {error && <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
      {!install.installed && (install.canPrompt ? (
        <Button size="small" variant="outlined" sx={{ mb: 1 }} onClick={() => promptInstall()}>Install the app</Button>
      ) : install.ios ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          To install: tap Share, then “Add to Home Screen”. Notifications on iPhone work from the installed app.
        </Typography>
      ) : null)}
      {pushSupported() ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          <FormControlLabel sx={{ flexGrow: 1 }}
            control={<Switch checked={!!push} disabled={busy || push === null} onChange={(e) => toggle(e.target.checked)} />}
            label="Notifications on this device (reminders, replies, assignments)" />
          {push && <Button size="small" onClick={test}>Test</Button>}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {install.ios && !install.installed ? 'Install the app to get notifications on this iPhone.' : 'This browser can’t show notifications from the CRM.'}
        </Typography>
      )}
    </Box>
  )
}

export default DeviceSettings
